var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};

// client/x-mail-editor.ts
import { RXController } from "@adornis/chemistry/controllers/RXController.js";
import { css } from "@adornis/chemistry/directives/css.js";
import "@adornis/chemistry/elements/components/x-button.js";
import "@adornis/chemistry/elements/components/x-flex.js";
import "@adornis/chemistry/elements/components/x-icon.js";
import "@adornis/chemistry/elements/components/x-loader";
import { XSnackbar } from "@adornis/chemistry/elements/components/x-snackbar.js";
import { xComponents } from "@adornis/chemistry/elements/x-components.js";
import { XDialog } from "@adornis/dialog/x-dialog.js";
import { FormField } from "@adornis/formfield/form-field.js";
import { FormController } from "@adornis/forms/x-form-controller.js";
import "@adornis/forms/x-input.js";
import "@adornis/popover/x-dropdown-multiselection.js";
import "@adornis/writer/x-tinymce.js";
import { html, nothing, render } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { ref } from "lit/directives/ref.js";
import { until } from "lit/directives/until.js";
import { of, switchMap, takeUntil } from "rxjs";
import { mailTemplateDefinitions } from "../db/mail-template-definition.js";
import { assembleMailTemplate, MailTemplate } from "./../db/mail-template.js";
import "./x-recipient-preview.js";
var XMailEditor = class extends FormField {
  constructor() {
    super(...arguments);
    this.recipients = [];
    this.value = new RXController(this, new MailTemplate({}));
    this._form = new FormController(this, this.value.observable, {
      success: async (selectedTemplate) => {
        try {
          await selectedTemplate.save();
          void XSnackbar.show("Mail Template erfolgreich gespeichert");
        } catch (e) {
          return XDialog.alert(`Es gab ein Problem beim Speichern des Mail Templates: ${e.message}`);
        }
      },
      error: (validationError) => XDialog.alert(`Es gab ein Problem beim Speichern des Mail Templates: ${validationError.message}`)
    });
    this._mailTemplateContentChange = new RXController(
      this,
      this.value.observable,
      (mailTemplate$) => mailTemplate$.pipe(switchMap((mailTemplate) => mailTemplate?.whenChanging ?? of(null)))
    );
    this.tinyEditorOptions = "bold italic underline | alignleft aligncenter alignright | removeformat";
  }
  async getVariableValues() {
    if (this.exampleVariableValues)
      return this.exampleVariableValues;
    if (!this.value.value)
      return {};
    const definition = mailTemplateDefinitions[this._form.document.emailType];
    if (!definition)
      return {};
    const variables = definition.variables;
    return Object.fromEntries(
      variables.map((key) => {
        if (key.toLowerCase().includes("name")) {
          if (["pre", "vor", "first"].some((k) => key.toLowerCase().includes(k)))
            return [key, "Max"];
          return [key, "Mustermann"];
        }
        if (["date", "datum"].some((k) => key.toLowerCase().includes(k)))
          return [key, "17.06.1999"];
        if (key.toLowerCase().includes("link"))
          return [key, '<a href="https://google.com/"> https://google.com/ </a>'];
        return [key, "Variablen-Wert"];
      })
    );
  }
  connectedCallback() {
    super.connectedCallback();
    this._mailTemplateContentChange.observable.pipe(takeUntil(this.disconnected)).subscribe(() => this.dispatchEvent(new CustomEvent("value-picked", { detail: { value: this.value.value } })));
  }
  render() {
    return xComponents(html`
      <x-flex space="lg" h100>
        ${this.hintContent()} ${this.variablesDisplay()}
        <x-flex horizontal space="md" wrap flex> ${this.editorContent()} ${this.previewContent()} </x-flex>
        ${this.attachments ? this.attachmentSection() : nothing}
        <x-flex start horizontal space="md"> ${this.saveButton()} ${this.deleteButton()} </x-flex>
      </x-flex>
    `);
  }
  editorContent() {
    if (this.disabled)
      return nothing;
    return xComponents(html`
      <x-flex class="wrapper" space="lg" flex> ${this.recipientPreview()} ${this.writerContent()} </x-flex>
    `);
  }
  recipientPreview() {
    return this.recipients.length ? xComponents(html`
          <x-flex space="sm">
            <x-h3 bold> Empfänger </x-h3>
            <x-recipient-preview .recipients=${this.recipients}></x-recipient-preview>
          </x-flex>
        `) : nothing;
  }
  writerContent() {
    if (this.disabled)
      return nothing;
    return xComponents(html`
      <x-flex space="md" flex>
        <x-h3> Nachricht </x-h3>
        <x-input ${this._form.field("subject")} placeholder="Betreff"></x-input>
        <x-tinymce
          flex
          placeholder="Hier E-Mail Text eingeben.."
          .editorOptions=${{ toolbar: this.tinyEditorOptions }}
          ${this._form.field("content")}
        ></x-tinymce>
        ${this.footer ? html`<x-tinymce
              flex
              placeholder="Hier E-Mail Footer eingeben.."
              .editorOptions=${{ toolbar: this.tinyEditorOptions }}
              ${this._form.field("footer")}
            ></x-tinymce>` : nothing}
      </x-flex>
    `);
  }
  hintContent() {
    const definition = mailTemplateDefinitions[this._form.document.emailType];
    if (!definition)
      return nothing;
    return html` <x-text> ${definition.editorHint()} </x-text> `;
  }
  saveButton() {
    if (this.disabled)
      return nothing;
    if (this.value.value?._id)
      return xComponents(html`
        <x-button icon="save" @click=${async (e) => this._form.validate({ mouseEvent: e })}>
          E-Mail Vorlage Speichern
        </x-button>
      `);
    return nothing;
  }
  deleteButton() {
    if (this.disabled)
      return nothing;
    if (this.value.value?._id && this.clearable)
      return xComponents(
        html`
          <x-button
            mode="outline"
            icon="delete"
            @click=${async () => {
          const template = this.value.value;
          if (!template)
            return;
          return XDialog.guard(() => template.remove(), {
            hasToConfirm: true,
            confirmText: "Dieses Mail-Template wirklich l\xF6schen?",
            success: () => {
              this.dispatchEvent(new CustomEvent("delete"));
            }
          });
        }}
          >
            Vorlage löschen
          </x-button>
        `
      );
    return nothing;
  }
  variablesDisplay() {
    return xComponents(html`
      ${until(
      this.getVariableValues().then((variableValues) => {
        const variableNames = Object.keys(variableValues);
        if (!variableNames.length)
          return nothing;
        return html`
            <x-toggle-content .label=${"M\xF6gliche Variablen: "} .open=${true}>
              <x-flex space="sm" ${css({ color: this.colors.neutral })}>
                <x-flex space="xs">
                  <x-flex horizontal wrap space="xs">
                    ${variableNames.map(
          (variableName) => html`
                          <x-chip
                            title="Klicken zum kopieren"
                            ${css({ cursor: "pointer" })}
                            @click=${async () => {
            try {
              await navigator.clipboard.writeText(`{{${variableName}}}`);
              await XSnackbar.show("Variable kopiert");
            } catch {
            }
          }}
                          >
                            ${variableName}
                          </x-chip>
                        `
        )}
                  </x-flex>
                </x-flex>
                <x-text> Variablen müssen im folgendem Format angegeben werden: {{vorname}} </x-text>
              </x-flex>
            </x-toggle-content>
          `;
      }),
      html` <x-loader></x-loader> `
    )}
    `);
  }
  previewContent() {
    const value = this.value.value;
    if (!value)
      return nothing;
    const previewContent = this.getVariableValues().then(
      (variableValues) => assembleMailTemplate({ template: value, infos: variableValues }).content
    );
    return xComponents(html`
      <x-flex ${css({ minWidth: "400px" })} space="sm" flex>
        <x-h3> Vorschau </x-h3>
        <x-text ${css({ color: this.colors.tone.warning })}>
          <b>Hinweis:</b> Sie müssen einmal aus dem Textfeld klicken damit sich die Vorschau aktualisiert.
        </x-text>
        <iframe
          srcdoc="<html> <head></head> <body></body> </html>"
          frameborder="0"
          flex
          ${ref((iframe) => {
      if (!iframe)
        return;
      setTimeout(async () => {
        const iframeBody = iframe.contentWindow?.document.body;
        if (!iframeBody)
          return;
        render(await previewContent, iframeBody);
      }, 250);
    })}
        ></iframe>
      </x-flex>
    `);
  }
  attachmentSection() {
    if (this.disabled)
      return nothing;
    const definition = mailTemplateDefinitions[this._form.document.emailType];
    if (!definition)
      return nothing;
    const availableAttachments = definition.attachments;
    return html`
      ${availableAttachments.length ? html`
            <x-dropdown-multiselection
              .selectables=${availableAttachments}
              placeholder="Anhänge"
              placeholder-mode="inline-floating"
              .value=${this._form.document?.activatedAttachments ?? []}
              @value-picked=${(e) => this._form.document.activatedAttachments = e.detail.value ?? []}
            ></x-dropdown-multiselection>
          ` : nothing}
    `;
  }
};
__decorateClass([
  property({ type: Array })
], XMailEditor.prototype, "recipients", 2);
__decorateClass([
  property({ attribute: false })
], XMailEditor.prototype, "exampleVariableValues", 2);
__decorateClass([
  property({ attribute: false })
], XMailEditor.prototype, "buildMailOptions", 2);
__decorateClass([
  property({ type: Boolean })
], XMailEditor.prototype, "clearable", 2);
__decorateClass([
  property({ type: Boolean })
], XMailEditor.prototype, "footer", 2);
__decorateClass([
  property({ type: Boolean })
], XMailEditor.prototype, "attachments", 2);
__decorateClass([
  state()
], XMailEditor.prototype, "_form", 2);
__decorateClass([
  state()
], XMailEditor.prototype, "_mailTemplateContentChange", 2);
XMailEditor = __decorateClass([
  customElement("x-mail-editor")
], XMailEditor);
export {
  XMailEditor
};
